import React from "react";
import { IMAGE_URL } from "../../../path";

interface GalleryImage {
    file?: File | null;
    preview?: string; // relative path or blob URL
    caption?: string;
}

interface AddGalleryComponentProps {
    section: {
        id: number;
        title?: string;
        images?: GalleryImage[];
    };
    onChange: (id: number, updatedData: Partial<any>) => void;
    onClose: () => void;
}

const AddGalleryComponent: React.FC<AddGalleryComponentProps> = ({
                                                                     section,
                                                                     onChange,
                                                                     onClose,
                                                                 }) => {
    const images = section.images || [];

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(section.id, { title: e.target.value });
    };

    const handleFilesChange = (files: FileList | null) => {
        if (!files) return;
        const newFiles = Array.from(files).map((f) => ({
            file: f,
            preview: URL.createObjectURL(f),
        }));
        const updatedImages = [...images, ...newFiles];
        onChange(section.id, { images: updatedImages });
    };

    const handleSingleFileChange = (index: number, file: File) => {
        const updatedImages = [...images];
        updatedImages[index] = {
            ...updatedImages[index],
            file,
            preview: URL.createObjectURL(file),
        };
        onChange(section.id, { images: updatedImages });
    };

    const removeImage = (index: number) => {
        const updatedImages = images.filter((_, i) => i !== index);
        onChange(section.id, { images: updatedImages });
    };

    const getImageSrc = (img: GalleryImage) => {
        if (img.file) return URL.createObjectURL(img.file);
        if (!img.preview) return "";
        if (
            img.preview.startsWith("blob:") ||
            img.preview.startsWith("data:") ||
            img.preview.startsWith("http")
        )
            return img.preview;
        return `${IMAGE_URL}${img.preview}`;
    };

    return (
        <div
            style={{
                padding: "10px",
                position: "relative",
                marginBottom: "20px",
                border: "1px solid #ddd",
                borderRadius: "5px",
            }}
        >
            <button
                style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontSize: 18,
                }}
                onClick={onClose}
            >
                ✕
            </button>

            <input
                type="text"
                value={section.title || ""}
                onChange={handleTitleChange}
                placeholder="Gallery Title"
                className="form-control"
                style={{ marginBottom: "10px", padding: "8px" }}
            />

            <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleFilesChange(e.target.files)}
                style={{ marginBottom: "15px" }}
            />

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: "1rem",
                    marginTop: "10px",
                }}
            >
                {images.map((img, idx) => {
                    const src = getImageSrc(img);
                    if (!src) return null;
                    return (
                        <div
                            key={idx}
                            style={{
                                position: "relative",
                                aspectRatio: "1",
                                border: "1px solid #ddd",
                                borderRadius: "5px",
                                overflow: "hidden",
                            }}
                        >
                            <img
                                src={src}
                                alt={`Gallery ${idx + 1}`}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />

                            <input
                                type="file"
                                accept="image/*"
                                style={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    opacity: 0,
                                    width: "100%",
                                    height: "100%",
                                    cursor: "pointer",
                                }}
                                onChange={(e) =>
                                    e.target.files && handleSingleFileChange(idx, e.target.files[0])
                                }
                            />

                            <button
                                type="button"
                                onClick={() => removeImage(idx)}
                                style={{
                                    position: "absolute",
                                    top: 5,
                                    right: 5,
                                    background: "rgba(255,255,255,0.8)",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: 24,
                                    height: 24,
                                    cursor: "pointer",
                                    color: "#ff4444",
                                    fontWeight: "bold",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AddGalleryComponent;
